import { QueryClient, UseMutationResult } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods, _OrgStaffData, _Payload, _UserType } from '../constants/staticTypes';
import { addOrgStaffApi, getOrgStaffByIdApi, getOrgStaffListApi, updateOrgStaffStatusApi } from '../constants/apiEndPoints';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';

export const getOrgStaffList = (queryClient: QueryClient, page: number, limit: number, search?: string) => {
    let url = `${getOrgStaffListApi}?limit=${limit}&page=${page}`;
    if (search) {
        url += search;
    }

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export type _AddOrgStaffFormType = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string | null;
    staffPermissionRoles: string;
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType: _UserType;
    venues: [];
    id?: string; // to be used when editing staff
    signedUrl?: string;
};

export type _OrgStaffVenueForm = {
    venue: {
        id: string;
        venueName: string;
    };
    permissionGroup: {
        id: string;
        permissionRoleName: string;
    };
};

export const orgStaffVenueFormInitialValues: _OrgStaffVenueForm = {
    venue: {
        id: '',
        venueName: '',
    },
    permissionGroup: {
        id: '',
        permissionRoleName: '',
    },
};

export const initialAddOrgStaffDataValues: _AddOrgStaffFormType = {
    loginUserName: '',
    password: '',
    preferredName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    hireDate: null,
    staffPermissionRoles: '',
    venues: [],
    photoKey: '',
    sendPasswordCreation: true,
    passwordLessLoginOnly: true,
    sendPasswordCreationTo: 'email',
    userType: 'organizationStaff',
    signedUrl: '',
};

export const addOrgStaffValidationSchema = Yup.object({
    loginUserName: Yup.string().required('Username is required'),
    sendPasswordCreation: Yup.boolean(),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    preferredName: Yup.string().required('Display name is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
});

export const getAddOrgStaffApiFormattedData = (formData: _AddOrgStaffFormType) => {
    return {
        ...formData,
        staffPermissionRoles: [formData.staffPermissionRoles],
        venues: formData?.venues?.map((item: any) => {
            return {
                tenantId: item.venue.id,
                staffPermissionRoles: [item.permissionGroup.id],
            };
        }),
    };
};

export const addOrgStaff = (
    addOrgStaffMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _OrgStaffData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    addOrgStaffMutation.mutate(
        {
            url: addOrgStaffApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const editOrgStaff = (
    editOrgStaffMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _OrgStaffData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    editOrgStaffMutation.mutate(
        {
            url: addOrgStaffApi,
            method: _HttpMethods.PUT,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _OrgStaffId = {
    id: string;
};

export const getOrgStaffDetailByID = (
    getOrgStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _OrgStaffId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getOrgStaffDetailByIDMutation.mutate(
        {
            url: getOrgStaffByIdApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const deleteOrgStaffDetailByID = (
    getAdminStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _OrgStaffId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getAdminStaffDetailByIDMutation.mutate(
        {
            url: addOrgStaffApi,
            method: _HttpMethods.DELETE,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _UpdateStaffStatus = {
    id: string;
    newStatus: boolean;
};

export const updateOrgStaffStatus = (
    updateAdminStaffStatusMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateStaffStatus,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateAdminStaffStatusMutation.mutate(
        {
            url: updateOrgStaffStatusApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
